import ErrorState from '@/components/ErrorState';
import Layout from '@/components/Layout';
import LoadingState from '@/components/LoadingState';
import ProductDetails, { ProductData } from '@/components/ProductDetails';
import ProductSections from '@/components/ProductSections';
import { SEO } from '@/components/SEO';
import useProductData from '@/hooks/useProductData';
import { IContentWithImage } from '@/utils/types';
import { Box, Text } from '@everlywell/ui-kit';
import { graphql, HeadFC, PageProps } from 'gatsby';
import React from 'react';

type Props = {
  data: {
    contentfulPage001: {
      slug: string;
      sections: {
        headline: string;
        title: string;
        subHeadline: string;
        componentName: string;
        slug: string;
        backgroundColor?: string;
        description?: {
          description?: string;
          childMarkdownRemark: {
            rawMarkdownBody: string;
          };
        };
        disclaimer?: {
          childMarkdownRemark: {
            rawMarkdownBody: string;
            html: string;
          };
        };
        sectionImage?: {
          title: string;
          url: string;
          gatsbyImageData: any;
        };
        cta?: {
          text: string;
          url: string;
        };
        listOfContentWithImages?: IContentWithImage[];
        container?: any;
      }[];
      hero: {
        __typename: string;
        id: string;
        productName: string;
        eyebrow: string;
        heading: string;
        description: {
          description: string;
        };
        media: {
          media: {
            file: {
              url: string;
              contentType: string;
              details: {
                image: {
                  width: number;
                  height: number;
                };
              };
            };
            gatsbyImageData: any;
          };
          videoThumb?: {
            file: {
              url: string;
            };
          };
          subtitles?: {
            file: {
              url: string;
            };
          };
          title: string;
          overlayText?: string;
          overlayDisclaimer?: string;
        };
        primaryButton?: {
          text: string;
          url: string;
        };
        secondaryButton?: {
          text: string;
          url: string;
        };
        tabs?: Array<{
          title: string;
          description: {
            raw: string;
          };
          measures?: string[];
          sampleTypes?: Array<{
            text: string;
            icon: {
              file: {
                url: string;
              };
            };
          }>;
          accordions?: Array<{
            title: string;
            description: {
              raw: string;
            };
            sections?: Array<{
              title: string;
              content: {
                content: string;
              };
            }>;
          }>;
        }>;
      };
    };
  };
};

type PageContext = {
  slug: string;
};

/**
 * Transform the GraphQL product details data into the ProductData type
 */
const transformProductDetailsData = (
  data: Props['data']['contentfulPage001']['hero'] | undefined,
): ProductData | undefined => {
  if (!data || data.__typename !== 'ContentfulProductDetails') return undefined;

  const productData: ProductData = {
    product: {
      name: data.eyebrow || '',
      title: data.heading || '',
      shortDescription: data.description?.description || '',
      buttons: {
        primary: {
          text: data.primaryButton?.text || '',
          link: data.primaryButton?.url || '',
        },
        secondary: {
          text: data.secondaryButton?.text || '',
          link: data.secondaryButton?.url || '',
        },
      },
      media: {
        video: {
          url: data.media?.media?.file?.url || '',
          videoThumb: data.media?.videoThumb?.file?.url || '',
          subtitles: data.media?.subtitles?.file?.url || '',
          title: data.media?.title || '',
          videoText: data.media?.overlayText || '',
          disclaimer: data.media?.overlayDisclaimer || '',
        },
        image: data.media?.media?.gatsbyImageData,
      },
    },
    tabs:
      data.tabs?.map((tab) => ({
        title: tab.title || '',
        description: tab.description?.raw
          ? JSON.parse(tab.description.raw)
          : null,
        measures: tab.measures || [],
        sampleTypes:
          tab.sampleTypes?.map((type) => ({
            text: type.text || '',
            icon: type.icon?.file?.url || '',
          })) || [],
        productAccordions:
          tab.accordions?.map((accordion) => ({
            title: accordion.title || '',
            description: accordion.description?.raw
              ? [JSON.parse(accordion.description.raw)]
              : [],
            sections:
              accordion.sections?.map((section) => ({
                title: section.title || '',
                content: section.content?.content || '',
              })) || [],
          })) || [],
      })) || [],
  };

  return productData;
};

/**
 * Product page component that displays product details and related sections
 */
const ProductPage: React.FC<PageProps<Props['data'], PageContext>> = ({
  data,
  params,
  pageContext,
}) => {
  const sections = data?.contentfulPage001?.sections || [];
  const hero = data?.contentfulPage001?.hero;
  const productDetails = transformProductDetailsData(hero);
  const slugFromContext = pageContext?.slug || '';
  const slugFromParams = params?.slug || '';

  const rawSlug = slugFromContext || slugFromParams;
  const slug = rawSlug.startsWith('/') ? rawSlug : `/products/${rawSlug}/`;

  // Use our custom hook to fetch product data
  const { productId, loading, error } = useProductData({
    slug,
    pageContext,
  });

  // Handle loading state
  if (loading) {
    return (
      <Layout>
        <LoadingState />
      </Layout>
    );
  }

  // Handle error state but still show sections if available
  if (error || !productId) {
    if (sections && sections.length > 0) {
      return (
        <Layout>
          <ProductSections sections={sections} />
        </Layout>
      );
    }

    // If no sections data, show error
    return (
      <Layout>
        <ErrorState message={error || 'Product not found'} />
      </Layout>
    );
  }

  // Main render with product details and sections
  return (
    <Layout>
      <ProductDetails data={productDetails} />
      {sections && sections.length > 0 ? (
        <ProductSections sections={sections} />
      ) : (
        <Box
          as="section"
          pt={['2rem', '3rem']}
          mb="3rem"
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Text color="gray.500" fontSize="lg">
            No additional sections available for this product
          </Text>
        </Box>
      )}
    </Layout>
  );
};

export default ProductPage;

/**
 * Head component for SEO
 */
export const Head: HeadFC<object, PageContext> = ({ params, pageContext }) => {
  const slugFromContext = pageContext?.slug || '';
  const slugFromParams = params?.slug || '';
  const slug = slugFromContext || slugFromParams || '';

  const productSlug = slug.includes('/') ? slug.split('/').pop() || slug : slug;

  const productName = productSlug
    .replace(/-/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return <SEO title={`${productName} - Everly Health Solutions`} />;
};

export const query = graphql`
  query ProductPageQuery($slug: String!) {
    contentfulPage001(slug: { eq: $slug }) {
      slug
      sections {
        headline
        title
        subHeadline
        componentName
        slug
        backgroundColor
        description {
          description
        }
        disclaimer {
          childMarkdownRemark {
            html
            rawMarkdownBody
          }
        }
        sectionImage {
          title
          url
          gatsbyImageData(placeholder: BLURRED, quality: 75)
        }
        cta {
          text
          url
        }
        listOfContentWithImages {
          ... on ContentfulContentWithImage {
            heading
            description {
              childMarkdownRemark {
                rawMarkdownBody
                html
              }
            }
            image {
              url
            }
          }
        }
        container {
          ... on ContentfulStatsBar {
            textColor
            statsCollection {
              prefix
              suffix
              value
              label
            }
          }
          ... on ContentfulSlider {
            slides {
              slideHeading: heading
              description {
                childMarkdownRemark {
                  rawMarkdownBody
                }
              }
              backgroundImage {
                file {
                  url
                }
              }
              mobileBackgroundImage {
                file {
                  url
                }
              }
              ctaButton {
                url
                label
              }
              features {
                icon {
                  url
                }
                text
              }
              navigationLabel
            }
          }
          ... on ContentfulHealthSolutionCard {
            icon {
              url
            }
            title
            description
            link
          }
          ... on ContentfulComparisonTable {
            memberA {
              title
              subtitle
              image {
                description
                gatsbyImageData(layout: CONSTRAINED, width: 400, quality: 80)
              }
            }
            memberB {
              title
              subtitle
              image {
                description
                gatsbyImageData(layout: CONSTRAINED, width: 400, quality: 80)
              }
            }
            rows {
              id
              label
              memberAContent {
                memberAContent
              }
              memberBContent {
                memberBContent
              }
              colorMemberA
              colorMemberB
            }
          }
        }
      }
      hero {
        __typename
        ... on ContentfulProductDetails {
          id
          productName
          eyebrow
          heading
          description {
            description
          }
          media {
            media {
              file {
                url
                contentType
                details {
                  image {
                    width
                    height
                  }
                }
              }
              gatsbyImageData(width: 800)
            }
            videoThumb {
              file {
                url
              }
            }
            subtitles {
              file {
                url
              }
            }
            title
            overlayText
            overlayDisclaimer
          }
          primaryButton {
            text
            url
          }
          secondaryButton {
            text
            url
          }
          tabs {
            title
            description {
              raw
            }
            measures
            sampleTypes {
              text
              icon {
                file {
                  url
                }
              }
            }
            accordions {
              title
              description {
                raw
              }
              sections {
                title
                content {
                  content
                }
              }
            }
          }
        }
      }
    }
  }
`;
